/**
 * Auth Debugger Utility
 * 
 * This utility provides functions to help debug sign-in issues.
 */

import { reload, getIdToken } from '../staffAuthApi';

/**
 * Clears old auth cache from localStorage and sessionStorage
 * @returns {Object} Object containing the number of items cleared
 */
export const clearAuthCache = () => {
  let localStorageCleared = 0;
  let sessionStorageCleared = 0;
  
  // Clear old auth cache keys left in the browser
  const specificKeys = [
    'firebase:authUser',
    'firebase:previousUser',
    'firebase:pendingRedirect',
    'firebase:persistence'
  ];
  
  // Clear from localStorage
  specificKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      localStorageCleared++;
    }
  });
  
  // Clear from sessionStorage
  specificKeys.forEach(key => {
    if (sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key);
      sessionStorageCleared++;
    }
  });
  
  // Find and clear any other old auth cache keys
  const authRelatedPatterns = [
    /firebase:/,
    /auth/i,
    /user/i,
    /token/i,
    /credential/i,
    /session/i
  ];
  
  // Check localStorage
  Object.keys(localStorage).forEach(key => {
    if (authRelatedPatterns.some(pattern => pattern.test(key))) {
      localStorage.removeItem(key);
      localStorageCleared++;
    }
  });
  
  // Check sessionStorage
  Object.keys(sessionStorage).forEach(key => {
    if (authRelatedPatterns.some(pattern => pattern.test(key))) {
      sessionStorage.removeItem(key);
      sessionStorageCleared++;
    }
  });
  
  console.log(`Auth cache cleared: ${localStorageCleared} items from localStorage, ${sessionStorageCleared} items from sessionStorage`);
  
  return {
    localStorageCleared,
    sessionStorageCleared,
    totalCleared: localStorageCleared + sessionStorageCleared
  };
};

/**
 * Refreshes the user's authentication state.
 * After reload, reads emailVerified from auth.currentUser so we always get the server value.
 * @param {Object} user - Signed-in session user
 * @param {Object} [authInstance] - Optional auth instance; if provided, emailVerified is read from authInstance.currentUser after reload
 * @returns {Promise<Object>} Object containing the refreshed user and token
 */
export const refreshUserAuth = async (user, authInstance = null) => {
  if (!user) {
    throw new Error('No user provided to refreshUserAuth');
  }
  
  try {
    await reload(user);
    // Force token refresh
    const token = await getIdToken(user, true);
    // Use auth.currentUser so we always have the live server value (custom verification updates server-side)
    const source = authInstance?.currentUser || user;
    const emailVerified = source?.emailVerified === true;
    return {
      user,
      token,
      emailVerified,
      success: true
    };
  } catch (error) {
    console.error('Error refreshing user auth:', error);
    return {
      error,
      success: false
    };
  }
};

/**
 * Formats auth errors into user-friendly messages
 * @param {Error} error - Auth error
 * @returns {String} User-friendly error message
 */
export const formatAuthError = (error) => {
  if (!error) return 'Unknown error occurred';
  
  const errorCode = error.code || '';
  const errorMessage = error.message || 'An authentication error occurred';
  
  // Map common auth error codes to user-friendly messages
  const errorMap = {
    'auth/user-not-found': 'No account found with this email address',
    'auth/wrong-password': 'Incorrect password',
    'auth/invalid-email': 'Invalid email address format',
    'auth/user-disabled': 'This account has been disabled',
    'auth/email-already-in-use': 'An account with this email already exists',
    'auth/weak-password': 'Password is too weak. Please use a stronger password',
    'auth/operation-not-allowed': 'This operation is not allowed',
    'auth/account-exists-with-different-credential': 'An account already exists with the same email but different sign-in credentials',
    'auth/invalid-credential': 'The credential is malformed or has expired',
    'auth/invalid-verification-code': 'Invalid verification code',
    'auth/invalid-verification-id': 'Invalid verification ID',
    'auth/requires-recent-login': 'This operation requires recent authentication. Please log in again',
    'auth/too-many-requests': 'Too many unsuccessful login attempts. Please try again later',
    'auth/network-request-failed': 'Network error. Please check your internet connection',
    'auth/popup-closed-by-user': 'Sign-in popup was closed before completing the sign-in',
    'auth/unauthorized-domain': 'This domain is not authorized for OAuth operations',
    'auth/expired-action-code': 'The action code has expired',
    'auth/invalid-action-code': 'The action code is invalid',
    'auth/missing-verification-code': 'Missing verification code',
    'auth/missing-verification-id': 'Missing verification ID',
    'auth/quota-exceeded': 'Quota exceeded',
    'auth/captcha-check-failed': 'CAPTCHA verification failed',
    'auth/invalid-phone-number': 'Invalid phone number format',
    'auth/missing-phone-number': 'Phone number is required',
    'auth/provider-already-linked': 'Provider is already linked to the user',
    'auth/credential-already-in-use': 'This credential is already associated with a different user account',
    'auth/operation-not-supported-in-this-environment': 'This operation is not supported in the current environment',
    'auth/timeout': 'The operation has timed out',
    'auth/missing-android-pkg-name': 'Missing Android package name',
    'auth/missing-continue-uri': 'Missing continue URL',
    'auth/missing-ios-bundle-id': 'Missing iOS bundle ID',
    'auth/invalid-continue-uri': 'Invalid continue URL',
    'auth/invalid-dynamic-link-domain': 'Invalid dynamic link domain',
    'auth/invalid-oauth-client-id': 'Invalid OAuth client ID',
    'auth/invalid-oauth-provider': 'Invalid OAuth provider',
    'auth/invalid-recipient-email': 'Invalid recipient email',
    'auth/invalid-sender': 'Invalid sender',
    'auth/invalid-message-payload': 'Invalid message payload',
    'auth/invalid-tenant-id': 'Invalid tenant ID',
    'auth/missing-tenant-id': 'Missing tenant ID',
    'auth/tenant-id-mismatch': 'Tenant ID mismatch',
    'auth/admin-restricted-operation': 'This operation is restricted to administrators',
    'auth/app-not-authorized': 'This app is not authorized to sign in',
    'auth/app-not-installed': 'The requested mobile application corresponding to the identifier is not installed on this device',
    'auth/captcha-timeout': 'The CAPTCHA token has expired',
    'auth/cancelled-popup-request': 'This operation has been cancelled due to another conflicting popup being opened',
    'auth/internal-error': 'An internal authentication error has occurred',
    'auth/invalid-app-credential': 'The app credential is invalid',
    'auth/invalid-app-id': 'The app ID is invalid',
    'auth/invalid-user-token': 'The user\'s credential is no longer valid. Please log in again',
    'auth/invalid-auth-event': 'An internal authentication error has occurred',
    'auth/invalid-api-key': 'Your API key is invalid',
    'auth/invalid-cert-hash': 'The SHA-1 certificate hash provided is invalid',
    'auth/invalid-credential': 'The credential used to authenticate is invalid',
    'auth/invalid-message-payload': 'The email template corresponding to this action contains invalid characters in its message',
    'auth/invalid-multi-factor-session': 'The request does not contain a valid proof of first factor successful sign-in',
    'auth/missing-multi-factor-session': 'No session proof available for multi-factor authentication',
    'auth/missing-multi-factor-info': 'No second factor identifier is provided',
    'auth/missing-multi-factor-auth': 'The user does not have a second factor',
    'auth/second-factor-already-in-use': 'The second factor is already enrolled on this account',
    'auth/maximum-second-factor-count-exceeded': 'The maximum allowed number of second factors has been exceeded',
    'auth/unsupported-first-factor': 'Enrolling a second factor or signing in with a multi-factor account requires sign-in with a supported first factor',
    'auth/unsupported-second-factor': 'The requested second factor is not supported',
    'auth/unverified-email': 'The operation requires a verified email',
    'auth/user-cancelled': 'The user did not grant your application the permissions it requested',
    'auth/user-not-found': 'There is no user record corresponding to this identifier',
    'auth/user-token-expired': 'The user\'s credential has expired. Please log in again',
    'auth/web-storage-unsupported': 'This browser is not supported or 3rd party cookies and data may be disabled',
    'auth/already-initialized': 'Sign-in is already initialized'
  };
  
  // Return the mapped error message or the original message if no mapping exists
  return errorMap[errorCode] || errorMessage;
};

/**
 * Logs detailed information about the current auth state for debugging
 * @param {Object} auth - Auth instance
 * @returns {Object} Object containing debug information
 */
export const logAuthDebugInfo = (auth) => {
  if (!auth) {
    console.error('No auth instance provided to logAuthDebugInfo');
    return { error: 'No auth instance provided' };
  }
  
  const currentUser = auth.currentUser;
  const debugInfo = {
    hasCurrentUser: !!currentUser,
    currentUserInfo: currentUser ? {
      uid: currentUser.uid,
      email: currentUser.email,
      emailVerified: currentUser.emailVerified,
      isAnonymous: currentUser.isAnonymous,
      providerData: currentUser.providerData,
      metadata: {
        creationTime: currentUser.metadata?.creationTime,
        lastSignInTime: currentUser.metadata?.lastSignInTime
      }
    } : null,
    persistenceEnabled: !!localStorage.getItem('firebase:persistence'),
    localStorageAuthItems: Object.keys(localStorage).filter(key => 
      key.startsWith('firebase:') || key.includes('auth') || key.includes('user')
    ),
    sessionStorageAuthItems: Object.keys(sessionStorage).filter(key => 
      key.startsWith('firebase:') || key.includes('auth') || key.includes('user')
    )
  };
  
  console.log('Auth Debug Info:', debugInfo);
  return debugInfo;
};

/**
 * Formats MFA-specific errors into user-friendly messages
 * @param {Error} error - MFA error
 * @returns {String} User-friendly error message
 */
export const formatMfaError = (error) => {
  if (!error) return 'Unknown MFA error occurred';
  
  const errorCode = error.code || '';
  const errorMessage = error.message || 'An MFA error occurred';
  
  // Map common MFA error codes to user-friendly messages
  const mfaErrorMap = {
    'auth/invalid-verification-code': 'The verification code you entered is invalid. Please try again.',
    'auth/missing-verification-code': 'Please enter the verification code sent to your phone.',
    'auth/invalid-verification-id': 'The verification session has expired. Please try signing in again.',
    'auth/code-expired': 'The verification code has expired. Please request a new code.',
    'auth/quota-exceeded': 'Too many verification attempts. Please try again later.',
    'auth/missing-phone-number': 'No phone number is associated with this account for verification.',
    'auth/invalid-phone-number': 'The phone number format is invalid.',
    'auth/missing-multi-factor-session': 'The authentication session has expired. Please try signing in again.',
    'auth/missing-multi-factor-info': 'No second factor is available for verification.',
    'auth/invalid-multi-factor-session': 'The authentication session is invalid. Please try signing in again.',
    'auth/multi-factor-info-not-found': 'No matching second factor found.',
    'auth/second-factor-already-enrolled': 'This second factor is already enrolled.',
    'auth/maximum-second-factor-count-exceeded': 'The maximum number of second factors has been reached.',
    'auth/unsupported-first-factor': 'The first authentication factor is not supported.',
    'auth/unsupported-second-factor': 'The second authentication factor is not supported.',
    'auth/unverified-email': 'Email verification is required before setting up MFA.',
    'auth/requires-recent-login': 'This operation requires recent authentication. Please log in again.',
    'auth/captcha-check-failed': 'reCAPTCHA verification failed. Please try again.',
    'auth/internal-error': 'An internal authentication error occurred. Please try again later.',
    'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
    'auth/timeout': 'The operation has timed out. Please try again.',
    'auth/too-many-requests': 'Too many unsuccessful attempts. Please try again later.',
    'auth/user-cancelled': 'The verification was cancelled.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/user-token-expired': 'Your authentication session has expired. Please sign in again.',
    'auth/web-storage-unsupported': 'This browser is not supported or 3rd party cookies and data may be disabled.'
  };
  
  // Return the mapped error message or the original message if no mapping exists
  return mfaErrorMap[errorCode] || errorMessage;
}; 